import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleNotch, Trash, WarningCircle } from "@phosphor-icons/react";
import { FAQ_ICONS, DEFAULT_FAQ_ICON, FAQ_ICON_KEYS, type FaqIconKey } from "@/lib/faq-icons";
import { FAQ_PLACEMENTS, type FaqPlacement } from "@/lib/faq-categories";
import { FAQ_PLACEMENT_META } from "./placements";
import type { FaqCategory } from "@/types/faq";

export function FaqCategoryFormDialog({
  form,
  isSaving,
  isDeleting,
  onChange,
  onSave,
  onDelete,
  onClose,
}: {
  form: Partial<FaqCategory>;
  isSaving: boolean;
  isDeleting: boolean;
  onChange: (next: Partial<FaqCategory>) => void;
  onSave: () => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isEdit = Boolean(form.id);
  const placements = form.placements ?? [];
  const togglePlacement = (placement: FaqPlacement, checked: boolean) => {
    // Redoslijed drži FAQ_PLACEMENTS da CSV u bazi ne zavisi od reda klikanja.
    const next = FAQ_PLACEMENTS.filter((p) =>
      p === placement ? checked : placements.includes(p),
    );
    onChange({ ...form, placements: next });
  };
  const PreviewIcon = FAQ_ICONS[form.icon ?? DEFAULT_FAQ_ICON] ?? FAQ_ICONS[DEFAULT_FAQ_ICON];

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={PreviewIcon}
          title={isEdit ? t("faqAdmin.editCategory") : t("faqAdmin.newCategory")}
          description={t("faqAdmin.categoryDialogHint")}
        />
      </DialogHeader>

      <DialogBody>
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-3 rounded-xl border bg-muted/20 px-4 py-3">
            <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10">
              <PreviewIcon className="size-6 text-primary" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {form.nameBs || t("faqAdmin.nameBs")}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {form.nameEn || t("faqAdmin.nameEn")}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="faq-cat-bs">{t("faqAdmin.nameBs")}</Label>
              <Input
                id="faq-cat-bs"
                value={form.nameBs ?? ""}
                onChange={(e) => onChange({ ...form, nameBs: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faq-cat-en">{t("faqAdmin.nameEn")}</Label>
              <Input
                id="faq-cat-en"
                value={form.nameEn ?? ""}
                onChange={(e) => onChange({ ...form, nameEn: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("faqAdmin.icon")}</Label>
            <Select
              value={form.icon ?? DEFAULT_FAQ_ICON}
              onValueChange={(v) => onChange({ ...form, icon: v as FaqIconKey })}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FAQ_ICON_KEYS.map((key) => {
                  const Icon = FAQ_ICONS[key];
                  return (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <Icon className="size-4 text-primary" />
                        {key}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div>
              <Label>{t("faqAdmin.placements")}</Label>
              <p className="mt-1 text-xs text-muted-foreground">{t("faqAdmin.placementsHint")}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              {FAQ_PLACEMENTS.map((placement) => {
                const { icon: Icon, labelKey } = FAQ_PLACEMENT_META[placement];
                const checked = placements.includes(placement);
                return (
                  <label
                    key={placement}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg border bg-muted/10 px-3 py-2.5"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(next) => togglePlacement(placement, next === true)}
                    />
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium">{t(labelKey)}</span>
                  </label>
                );
              })}
            </div>
            {placements.length === 0 && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                <WarningCircle className="mt-0.5 size-3.5 shrink-0" weight="fill" />
                {t("faqAdmin.placementNoneHint")}
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border bg-muted/10 px-3 py-2.5">
            <Checkbox
              checked={form.isActive ?? true}
              onCheckedChange={(checked) => onChange({ ...form, isActive: checked === true })}
            />
            <span className="text-sm font-medium">{t("faqAdmin.active")}</span>
          </label>
        </div>
      </DialogBody>

      <DialogFooter>
        {isEdit && onDelete && (
          confirmDelete ? (
            <div className="flex w-full items-center justify-between gap-2 sm:mr-auto">
              <p className="text-sm font-medium text-rose-600">{t("faqAdmin.confirmDelete")}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                  {t("faqAdmin.cancel")}
                </Button>
                <Button variant="destructive" size="sm" onClick={onDelete} disabled={isDeleting}>
                  {isDeleting ? (
                    <>
                      <CircleNotch className="size-4 animate-spin" />
                      {t("faqAdmin.deleting")}
                    </>
                  ) : (
                    t("faqAdmin.delete")
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              <Trash className="size-4" />
              {t("faqAdmin.delete")}
            </Button>
          )
        )}
        {!confirmDelete && (
          <>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("faqAdmin.cancel")}
            </Button>
            <Button
              type="button"
              disabled={isSaving || !form.nameBs?.trim() || !form.nameEn?.trim()}
              onClick={onSave}
            >
              {isSaving ? (
                <>
                  <CircleNotch className="size-4 animate-spin" />
                  {t("faqAdmin.saving")}
                </>
              ) : (
                t("faqAdmin.save")
              )}
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
